import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../Constant';

const DATASET_LABELS = {
  asi: 'ASI',
  iip: 'IIP',
  price: 'Price',
};

const hasDistrictRows = (block) =>
  (block?.top5?.length || 0) + (block?.bottom5?.length || 0) > 0;

function pickPerformanceDataset(allData, preferredKey, allowFallback) {
  const selected = allData?.[preferredKey];
  if (hasDistrictRows(selected)) {
    return {
      key: preferredKey,
      top5: selected.top5 || [],
      bottom5: selected.bottom5 || [],
    };
  }

  if (allowFallback) {
    const fallbackOrder =
      preferredKey === 'asi'
        ? ['iip', 'price']
        : preferredKey === 'iip'
          ? ['asi', 'price']
          : ['asi', 'iip'];
    for (const key of fallbackOrder) {
      if (hasDistrictRows(allData?.[key])) {
        return {
          key,
          top5: allData[key].top5 || [],
          bottom5: allData[key].bottom5 || [],
        };
      }
    }
  }

  return {
    key: preferredKey,
    top5: [],
    bottom5: [],
  };
}

const TopDist = ({ userType = '', fullWidth = false, className }) => {
  const [districtData, setDistrictData] = useState({ top5: [], bottom5: [] });
  const [activeDatasetKey, setActiveDatasetKey] = useState('asi');
  const [loading, setLoading] = useState(true);
  const topDistDataRef = useRef({ fetchId: 0 });
  const containerClassName = className || (fullWidth ? "col-12 mb-4" : "col-xl-6 col-lg-6 mb-4");

  const getDatasetKey = (type) => {
    if (type === 'HQAdmin' || type === 'HQASI') return 'asi';
    if (type === 'IIP') return 'iip';
    if (type === 'Price') return 'price';
    return '';
  };

  const datasetKey = getDatasetKey(userType);
  const allowFallback = userType === 'HQAdmin';
  const datasetTitle = DATASET_LABELS[activeDatasetKey] || DATASET_LABELS[datasetKey] || 'ASI';

  const currentYear = new Date().getFullYear();
  const minSurveyYear = 2019;
  const surveyYearOptions = Array.from(
    { length: Math.max(0, currentYear - minSurveyYear + 1) },
    (_, i) => currentYear - i
  );

  const getDefaultSurveyYear = (key) => {
    if (key === 'asi') return Math.max(minSurveyYear, currentYear - 2);
    if (key === 'iip' || key === 'price') return Math.max(minSurveyYear, currentYear - 1);
    return Math.max(minSurveyYear, currentYear - 2);
  };

  const [surveyYear, setSurveyYear] = useState(() => getDefaultSurveyYear(datasetKey));

  useEffect(() => {
    if (!datasetKey) return;
    setSurveyYear(getDefaultSurveyYear(datasetKey));
    setActiveDatasetKey(datasetKey);
  }, [datasetKey]);

  const scoreLabel =
    activeDatasetKey === 'iip'
      ? 'Score: Accepted Units % (Accepted / Total).'
      : 'Score: Performance %.';

  const getDisplayScore = (district) => {
    if (activeDatasetKey === 'iip' && typeof district?.accepted_percentage === 'number') {
      return `${district.accepted_percentage}%`;
    }
    return district?.percentage_score || 'N/A';
  };

  useEffect(() => {
    if (!userType || !datasetKey || !surveyYear) {
      return;
    }

    const fetchId = ++topDistDataRef.current.fetchId;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}api/dashboard/top5-bottom5-districts-all-types`,
          { params: { survey_year: surveyYear } }
        );
        if (cancelled || fetchId !== topDistDataRef.current.fetchId) {
          return;
        }
        if (response.data && response.data.status && response.data.data) {
          const picked = pickPerformanceDataset(
            response.data.data,
            datasetKey,
            allowFallback
          );
          setActiveDatasetKey(picked.key);
          setDistrictData({ top5: picked.top5, bottom5: picked.bottom5 });
        } else {
          setActiveDatasetKey(datasetKey);
          setDistrictData({ top5: [], bottom5: [] });
        }
      } catch (e) {
        if (cancelled || fetchId !== topDistDataRef.current.fetchId) {
          return;
        }
        console.error('Error fetching district data:', e);
        setActiveDatasetKey(datasetKey);
        setDistrictData({ top5: [], bottom5: [] });
      } finally {
        if (!cancelled && fetchId === topDistDataRef.current.fetchId) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [userType, datasetKey, surveyYear, allowFallback]);

  const cardStyle = {
    transition: 'all 0.3s ease',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    borderLeft: '4px solid #ff8c00'
  };

  return (
    <div className={containerClassName}>
      <div className="card h-100" style={cardStyle}>
        <div className="card-header bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div>
              <h6 className="mb-0">
                <i className="fa fa-map-marker me-2" style={{ color: '#ff8c00' }}></i>
                Districts Performance ({datasetTitle})
              </h6>
              <small className="text-muted">{scoreLabel}</small>
            </div>

            <div className="text-end" style={{ minWidth: 120 }}>
              <label className="form-label mb-1" style={{ fontSize: 12 }}>
                Survey Year
              </label>
              <select
                className="form-select form-select-sm"
                value={surveyYear}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setSurveyYear(Number.isFinite(v) ? v : getDefaultSurveyYear(datasetKey));
                }}
              >
                {surveyYearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center text-muted">Loading...</div>
          ) : (
            !districtData || (!districtData.top5?.length && !districtData.bottom5?.length) ? (
              <div className="text-center text-muted">No data available</div>
            ) : (
              <>
                {districtData.top5 && districtData.top5.length > 0 && (
                  <>
                    <div className="mb-2 fw-bold" style={{ color: '#28a745' }}>Top 5 Districts</div>
                    {districtData.top5.map((district, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-1">{district.DISTRICT_NAME || 'N/A'}</h6>
                          <small className="text-muted">Rank {district.rank || index + 1}</small>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold" style={{ color: '#ff8c00' }}>
                            {getDisplayScore(district)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {districtData.bottom5 && districtData.bottom5.length > 0 && (
                  <>
                    <div className="mb-2 mt-3 fw-bold" style={{ color: '#dc3545' }}>Bottom 5 Districts</div>
                    {districtData.bottom5.map((district, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-1">{district.DISTRICT_NAME || 'N/A'}</h6>
                          <small className="text-muted">Rank {district.rank || index + 1}</small>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold" style={{ color: '#ff8c00' }}>
                            {getDisplayScore(district)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TopDist;
